import React, { useState, useEffect } from 'react';
import { 
  getAllRooms, 
  getAllRoomTypes, 
  createRoom, 
  updateRoom, 
  deleteRoom 
} from '../../api';

const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    room_type_id: '',
    room_number: '',
    is_active: true
  });

  useEffect(() => {
    fetchRooms();
    fetchRoomTypes();
  }, []);

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const fetchRooms = async () => {
    try {
      const data = await getAllRooms();
      setRooms(data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const data = await getAllRoomTypes();
      setRoomTypes(data);
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const roomData = {
        ...formData,
        room_type_id: parseInt(formData.room_type_id)
      };

      if (editingRoom) {
        await updateRoom(editingRoom.id, roomData);
        showNotification('Room updated successfully!', 'success');
      } else {
        await createRoom(roomData);
        showNotification('Room created successfully!', 'success');
      }
      
      fetchRooms();
      resetForm();
    } catch (error) {
      console.error('Error saving room:', error);
      showNotification('Error saving room. Please try again.', 'error');
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({
      room_type_id: room.room_type_id.toString(),
      room_number: room.room_number,
      is_active: room.is_active
    });
    setShowForm(true);
  };

  const handleDelete = (room) => {
    setItemToDelete(room);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteRoom(itemToDelete.id);
      fetchRooms();
      showNotification('Room deleted successfully!', 'success');
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting room:', error);
      showNotification('Error deleting room. Please try again.', 'error');
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      room_type_id: '',
      room_number: '',
      is_active: true
    });
    setEditingRoom(null);
    setShowForm(false);
  };

  const getRoomTypeName = (roomTypeId) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return roomType ? roomType.name : 'Unknown';
  };

  if (loading) {
    return <div className="loading">Loading rooms...</div>;
  }

  return (
    <div className="room-management">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="room-management-header">
        <h2>Room Management</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Add New Room
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingRoom ? 'Edit Room' : 'Add New Room'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Room Type:</label>
                <select
                  value={formData.room_type_id}
                  onChange={(e) => setFormData({...formData, room_type_id: e.target.value})}
                  required
                >
                  <option value="">Select Room Type</option>
                  {roomTypes.map(roomType => (
                    <option key={roomType.id} value={roomType.id}>
                      {roomType.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Room Number:</label>
                <input
                  type="text"
                  value={formData.room_number}
                  onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  Active
                </label>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingRoom ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete room <strong>"{itemToDelete?.room_number}"</strong>?</p>
            <p>This action cannot be undone.</p>
            <div className="form-actions">
              <button 
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Delete Room
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rooms-table">
        <table>
          <thead>
            <tr>
              <th>Room Number</th>
              <th>Room Type</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map(room => (
              <tr key={room.id}>
                <td>{room.room_number}</td>
                <td>{getRoomTypeName(room.room_type_id)}</td>
                <td>
                  <span className={`status ${room.is_active ? 'active' : 'inactive'}`}>
                    {room.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button 
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(room)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(room)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoomManagement;
